import { useEffect, useState } from "react";
import { Bell, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Row } from "@/components/settings/Grouped";
import { useAuth } from "@/context/AuthContext";
import { disablePush, enablePush, isIos, isPhone, pushState } from "@/lib/push";

// 424: Nastavení › Upozornění — jedno tlačítko „Zapnout upozornění na tomto telefonu“ (varianta B
// bez náhledu). Na displej pak chodí označení @, komentář u mého úkolu a komentář k mé směně.
// Zvuk a Nerušit řídí telefon. „Poslat zkušební“ jen testovací firmy (Superadmin › Testovací účet).
export function PushRows() {
  const { company } = useAuth() || {};
  const [st, setSt] = useState("loading");
  const [busy, setBusy] = useState(false);
  const dev = isPhone() ? "telefonu" : "počítači";
  useEffect(() => { let live = true; pushState().then((s) => live && setSt(s)).catch(() => live && setSt("unsupported")); return () => { live = false; }; }, []);

  const turnOn = async () => {
    setBusy(true);
    try {
      const s = await enablePush();
      setSt(s);
      if (s === "on") toast.success("Upozornění zapnutá. Přijdou i se zavřenou aplikací.");
      else if (s === "denied") toast.error(`Upozornění jsou v ${isIos() ? "iPhonu" : "prohlížeči"} zakázaná — povol je v nastavení.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upozornění se nepodařilo zapnout."); }
    finally { setBusy(false); }
  };
  const turnOff = async () => {
    setBusy(true);
    try { setSt(await disablePush()); toast.success(`Na tomto ${dev} vypnuto.`); }
    finally { setBusy(false); }
  };
  const test = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(data.sent ? "Zkušební upozornění odesláno." : "Na žádné zařízení se nedoručilo — zapni upozornění.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sub = st === "on" ? <span className="text-success font-semibold">Zapnuto · přijde i se zavřenou aplikací</span>
    : st === "ios-home" ? "Na iPhonu nejdřív přidej Proyekty na plochu"
    : st === "denied" ? <span className="text-destructive font-semibold">Zakázané v nastavení {isIos() ? "iPhonu" : "telefonu / prohlížeče"}</span>
    : st === "unsupported" ? "Tento prohlížeč upozornění na displej neumí"
    : "Označení @ a komentáře u tvých úkolů a docházky";
  return (
    <>
      <Row label={`Upozornění na tomto ${dev}`} sub={st === "loading" ? " " : sub} wrap testid="push-row">
        {st === "on" ? (
          <Button variant="outline" onClick={turnOff} disabled={busy} className="h-10 px-4 font-bold shrink-0 w-full sm:w-auto" data-testid="push-off">Vypnout</Button>
        ) : st === "off" ? (
          <Button onClick={turnOn} disabled={busy} className="h-10 px-4 font-bold shrink-0 w-full sm:w-auto" data-testid="push-on"><Bell size={16} /> Zapnout upozornění</Button>
        ) : null}
      </Row>
      {(st === "ios-home" || st === "denied") && (
        <div className="px-3.5 md:px-4 py-3 text-[13px] bg-amber-500/[0.07]" data-testid={`push-help-${st}`}>
          {st === "ios-home" ? (
            <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
              <li>Dole v Safari klepni na <b className="text-foreground">Sdílet</b>.</li>
              <li>Vyber <b className="text-foreground">Přidat na plochu</b>.</li>
              <li>Otevři Proyekty z plochy a zapni upozornění tady.</li>
            </ol>
          ) : (
            <p className="text-muted-foreground">
              {isIos() ? <>Otevři <b className="text-foreground">Nastavení › Oznámení › Proyekty</b> a zapni <b className="text-foreground">Povolit oznámení</b>.</>
                : isPhone() ? <>V Chromu klepni na <b className="text-foreground">⋮ › Nastavení › Nastavení webu › Oznámení</b> a povol Proyekty.</>
                : <>Klikni na <b className="text-foreground">zámek vlevo od adresy</b> a u Oznámení zvol <b className="text-foreground">Povolit</b>.</>}
              {" "}Pak stránku obnov a zapni upozornění.
            </p>
          )}
        </div>
      )}
      {company?.is_test && st === "on" && (
        <Row label="Vyzkoušet" sub={`Pošle ukázku na tento ${isPhone() ? "telefon" : "počítač"} · jen testovací účet`} wrap testid="push-test-row">
          <Button variant="outline" onClick={test} className="h-10 px-4 font-bold shrink-0 w-full sm:w-auto" data-testid="push-test"><Send size={15} /> Poslat zkušební</Button>
        </Row>
      )}
    </>
  );
}
